import { useEffect, useState } from 'react';
import {
  NavLink, useLocation, useParams, useSearchParams,
} from 'react-router-dom';
import {
  DataGrid, frFR, enUS, GridCellCheckboxRenderer, GridColDef, GridHeaderCheckbox, GridSelectionModel, GridToolbar, GRID_CHECKBOX_SELECTION_COL_DEF,
} from '@mui/x-data-grid';
import {
  Box, Button, Chip, Typography,
} from '@mui/material';
import Layout from '../components/layout/Layout';
import Spinner from '../components/Spinner';
import Utils from '../utils/Utils';
import translator from '../theme/translator.json';
import { useAppDispatch, useAppSelector } from '../store/Hooks';
import SupportedLanguages from '../models/enums/SupportedLanguages';
import AlertUtils from '../utils/AlertUtil';
import ClientTypes from '../models/enums/ClientTypes';
import DeleteClientConfirmation from '../common/DeleteClientConfirmation';
import TransferClientsDialog from '../components/TransferClientsDialog';
import {
  setClientOpenId, setClientPopupOpen, setNavDisabled, setOrderedClientsList, setSearchQuery,
} from '../store/slices/ClientSlice';
import Roles from '../models/enums/Roles';

const Clients: React.FC = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();

  const language = useAppSelector((state) => state.user.language) as SupportedLanguages;
  const { clients, user } = useAppSelector((state) => state.user);
  const { searchQuery } = useAppSelector((state) => state.client);
  const isImpersonating = useAppSelector((state) => state.shadowUser.isImpersonating);

  const [rows, setRows] = useState([]) as any[];
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [selectedClients, setSelectedClients] = useState<any>([]);
  const [deleteClientsConfirmationOpen, setDeleteClientsConfirmationOpen] = useState(false);
  const [transferClientsDialogOpen, setTransferClientsDialogOpen] = useState(false);
  const [showSecondNav, setShowSecondNav] = useState(false);
  const [title, setTitle] = useState('Clients');

  const { filter, secondnav } = useParams();
  const { state } = useLocation();

  const columns: GridColDef[] = [
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      width: (() => {
        if (!selectedClients.length) return 50;
        if (user?.role === Roles.USER || isImpersonating) return 250;
        return 150;
      })(),
      renderHeader: (params) => {
        const getHeaderWidth = () => {
          if (!selectedClients.length) return 50;
          if (user?.role === Roles.USER || isImpersonating) return 250;
          return 150;
        };

        return (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1, width: getHeaderWidth(), transition: 'width 200ms ease',
          }}>
            <GridHeaderCheckbox {...params} />
            {!!selectedClients.length && (
              <>
                {(user?.role === Roles.USER || isImpersonating) && (
                  <Button
                    color='primary'
                    variant='contained'
                    size='small'
                    onClick={() => setTransferClientsDialogOpen(true)}
                  >
                    {Utils.getTranslation(language, translator.general.transfer)}
                  </Button>
                )}
                <Button
                  color='error'
                  variant='contained'
                  size='small'
                  onClick={() => setDeleteClientsConfirmationOpen(true)}
                >
                  {Utils.getTranslation(language, translator.general.delete)}
                </Button>
              </>
            )}
          </Box>
        );
      },
      renderCell: (params) => (
        <Box sx={{
          display: 'flex', alignItems: 'center', width: (selectedClients.length) ? 150 : 50, transition: 'width 200ms ease',
        }}>
          <GridCellCheckboxRenderer {...params} />
        </Box>
      ),
    },
    {
      field: 'name',
      headerName: Utils.getTranslation(language, translator.pages.dashboard.tables.nomDuClient),
      width: 250,
      renderCell: (params) => (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <Typography sx={{
              maxWidth: 180,
              overflow: 'hidden',
              fontSize: '1em',
              fontWeight: 600,
              paddingRight: 1,
            }}>
              {params.row.name}
            </Typography>
          </Box>
        </Box>
      ),
    }];

  if (user && !user.hidePriority) {
    columns.push(
      {
        field: 'priority',
        headerName: Utils.getTranslation(language, translator.pages.dashboard.tables.priority),
        renderCell: (params) => (
          <Typography sx={{
            textAlign: 'center', width: '100%', fontSize: '1em', fontWeight: 600,
          }}>
            {params.row.priority !== 99 && params.row.priority}
          </Typography>
        ),
        sortComparator: (v1, v2) => {
          const s1 = v1 || 99;
          const s2 = v2 || 99;
          return s1 - s2;
        },
      },
    );
  }

  columns.push(
    {
      field: 'tags',
      headerName: Utils.getTranslation(language, translator.pages.dashboard.tables.categories),
      width: 150,
      disableExport: true,
      renderCell: (params) => (
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: '5px', justifyContent: 'flex-start', width: '100%', overflowX: 'auto', height: '100%',
        }}>
          {
            params.row.tags.map((tag: string, index: number) => (
              <Chip color='secondary' variant="outlined" size="small" key={index} label={tag} />
            ))
          }
        </Box>
      ),
      sortComparator: (v1, v2) => {
        const s1 = v1[0] || ' ';
        const s2 = v2[0] || ' ';
        return s1[0].localeCompare(s2[0]);
      },
    },
  );
  columns.push(
    {
      field: 'interlocuteur',
      headerName: Utils.getTranslation(language, translator.pages.dashboard.tables.interlocuteur),
      width: 200,
    },
  );
  columns.push(
    {
      field: 'phone',
      headerName: Utils.getTranslation(language, translator.pages.dashboard.tables.phone),
      width: 200,
    },
  );
  columns.push(
    {
      field: 'email',
      headerName: Utils.getTranslation(language, translator.pages.dashboard.tables.email),
      sortable: false,
      width: 200,
    },
  );
  if (user && user.showAllInBanqueContacts && user.showAdressField) {
    columns.push(
      {
        field: 'address',
        headerName: 'Adresse',
        sortable: false,
        width: 400,
      },
    );
  } else {
    columns.push(
      {
        field: 'notes',
        headerName: Utils.getTranslation(language, translator.pages.dashboard.tables.notes),
        sortable: false,
        width: 400,
      },
    );
  }

  useEffect(() => {
    const fromParam = searchParams.get('from');
    switch (fromParam) {
      case 'accountCreated':
        AlertUtils.createSuccessAlert(Utils.getTranslation(language, translator.successMessages.accountCreated), dispatch);
        break;
      default:
        break;
    }
  }, []);

  useEffect(() => {
    if (clients && user) {
      const clientsRows: any[] = [];
      let tempActiveFilter = secondnav || filter || '';
      setShowSecondNav(false);
      if (tempActiveFilter === 'listes') {
        tempActiveFilter = 'reserve';
        setShowSecondNav(true);
      }
      if (secondnav) setShowSecondNav(true);

      clients.forEach((client:any) => {
        // Allow users to set banque de contacts to display all contacts
        if (user.showAllInBanqueContacts && tempActiveFilter === 'reserve') {
          clientsRows.push(client);
        } else if (tempActiveFilter
          && (client.type === tempActiveFilter)
        ) {
          clientsRows.push(client);
        } else if (!tempActiveFilter
          && (client.type === ClientTypes.CLIENT)) {
          clientsRows.push(client);
        }
      });
      setRows(clientsRows);
    }
  }, [clients, filter, secondnav, user]);

  useEffect(() => {
    dispatch(setOrderedClientsList(rows));
    setLoading(false);
  }, [rows]);

  useEffect(() => {
    if (state && state.title) setTitle(state.title);
  }, [state]);

  return (
    <Layout title={title}>
      <Spinner show={loading}/>
      <Box sx={{
        minHeight: 'calc(100vh - 64px)', width: '100%', padding: showSecondNav ? '32px 12px 45px' : '32px 12px 12px', '@media (max-width:565px)': { padding: '8px 0 0' },
      }}>
        <Box sx={{ height: '100%', width: '100%' }}>
          {showSecondNav && <Box sx={{
            width: '100%', background: 'white', mb: '-5px', borderRadius: '4px', display: { xs: 'none', md: 'flex' },
          }}>
            <NavLink
              to="/clients/listes"
              className="client-nav-link"
              style={({ isActive }) => (isActive ? { background: '#F1F1E6', color: '#F55C47' } : { color: '#16213E' })
              }
              end
            >
              {Utils.getTranslation(language, translator.pages.dashboard.tables.banqueContacts)}
            </NavLink>
            <NavLink
              to="/clients/listes/peu-interessant"
              className="client-nav-link"
              style={({ isActive }) => (isActive ? { background: '#F1F1E6', color: '#F55C47' } : { color: '#16213E' })
              }
            >
              {Utils.getTranslation(language, translator.pages.dashboard.tables.contactsAttente)}
            </NavLink>
          </Box>}
          <DataGrid
            rows={rows}
            columns={columns}
            disableColumnSelector
            disableDensitySelector
            disableColumnMenu
            initialState={{
              filter: {
                filterModel: {
                  items: [],
                  quickFilterValues: searchQuery ? [searchQuery] : [],
                },
              },
            }}
            onFilterModelChange={(newModel) => {
              if (newModel.quickFilterValues && newModel.quickFilterValues.length > 0) {
                dispatch(setSearchQuery(newModel.quickFilterValues.join(' ')));
              } else {
                dispatch(setSearchQuery(''));
              }
            }}
            onRowClick={(param) => {
              dispatch(setClientOpenId(param.row.id));
              dispatch(setClientPopupOpen(true));
              dispatch(setNavDisabled(false));
            }}
            rowsPerPageOptions={[25, 50, 100]}
            experimentalFeatures={{ newEditingApi: true }}
            localeText={(language === SupportedLanguages.FR) ? frFR.components.MuiDataGrid.defaultProps.localeText : enUS.components.MuiDataGrid.defaultProps.localeText }
            sx={{
              '.MuiDataGrid-columnHeaderTitle': {
                fontWeight: 'bold',
                fontSize: '1em',
              },
              '.MuiTextField-root div': {
                border: '1px solid black',
                borderRadius: '4px',
                padding: '2px',
              },
              '.MuiDataGrid-cellContent': {
                fontSize: '0.9em',
              },
              background: 'white',
            }}
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
                printOptions: { disableToolbarButton: true },
              },
            }}
            checkboxSelection
            onSelectionModelChange={(newSelectionModel) => {
              setSelectionModel(newSelectionModel);
              setSelectedClients(rows.filter((row:any) => newSelectionModel.includes(row.id)));
            }}
            selectionModel={selectionModel}
            disableSelectionOnClick
          />
        </Box>
      </Box>
      <DeleteClientConfirmation
        open={deleteClientsConfirmationOpen}
        handleClose={() => setDeleteClientsConfirmationOpen(false)}
        clients={selectedClients}
      />
      <TransferClientsDialog
        open={transferClientsDialogOpen}
        onClose={() => setTransferClientsDialogOpen(false)}
        selectedClients={selectedClients}
        onTransferComplete={() => {
          setSelectionModel([]);
          setSelectedClients([]);
          AlertUtils.createSuccessAlert(
            Utils.getTranslation(language, translator.components.transferClientsDialog.transferComplete),
            dispatch,
          );
        }}
      />
    </Layout>
  );
};

export default Clients;
